import {
  useEffect, useState,
} from 'react'
import {
  Check, Loader2, Users,
} from 'lucide-react'
import {
  billingService,
  getApiErrorMessage,
  getApiErrorStatus,
  organizationService,
} from '../../services/api'
import type {
  MyInviteResponse,
} from '../../client'
import {
  API_BASE_URL,
} from '../../config'
import {
  usePricing,
} from '../../hooks/usePricing'
import {
  CHANNEL_LABELS,
} from '../../hooks/useOnboardingState'
import type {
  ConnectedChannel,
} from '../../hooks/useOnboardingState'
import {
  OnboardingWizardEvents,
  trackEvent,
} from '../../analytics/posthog'
import {
  CheckoutModal,
} from '../CheckoutModal'
import {
  useOnboardingDemo,
} from './demo/OnboardingDemoContext'

interface BillingStepProps {
  onCompleted: () => void
  // Oldest pending team invite for this account (from useOnboardingState).
  // When present the step offers joining that team — billing covered by the
  // team, no card — before the individual paywall.
  teamInvite: MyInviteResponse | null
  // What the user has already set up, for the paywall copy. All optional:
  // a resumed wizard has no in-session character, and the channel/scopes can
  // legitimately be absent. The copy degrades a clause at a time.
  characterName?: string | null | undefined
  connectedChannel: ConnectedChannel
  hasMailCalendarScopes: boolean
}

const isLocalDev = API_BASE_URL.includes('localhost')

/**
 * The day the first payment lands: today plus the trial length, which is when
 * checkout sets the Stripe trial to end. Month-and-day only — the year is
 * noise at ten days out.
 */
function formatFirstChargeDate(trialDays: number): string {
  const chargeDate = new Date()
  chargeDate.setDate(chargeDate.getDate() + trialDays)
  return chargeDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
}

export function BillingStep({
  onCompleted,
  teamInvite,
  characterName,
  connectedChannel,
  hasMailCalendarScopes,
}: BillingStepProps): JSX.Element {
  const { pricing, loading: pricingLoading } = usePricing()
  const demo = useOnboardingDemo()
  const [loading, setLoading] = useState(false)
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)
  // True once the user chose "set up my own billing" over the invite.
  const [inviteSkipped, setInviteSkipped] = useState(false)
  useEffect(() => {
    trackEvent(OnboardingWizardEvents.BILLING_STEP_VIEWED)
  }, [])

  const showInviteOffer = teamInvite != null && !inviteSkipped

  useEffect(() => {
    if (teamInvite != null && !inviteSkipped) {
      trackEvent(OnboardingWizardEvents.BILLING_TEAM_INVITE_VIEWED, {
        organization_id: teamInvite.organization_id,
      })
    }
  }, [showInviteOffer])

  const handleStartTrial = async () => {
    trackEvent(OnboardingWizardEvents.BILLING_START_TRIAL_CLICKED)
    if (demo) {
      demo.startTrial()
      onCompleted()
      return
    }
    setLoading(true)
    setError(null)
    try {
      // The card leads with "Free for N days", demotes the price and drops
      // the card icon — then opens a Stripe form asking for a card and a full
      // billing address. Nothing of ours renders inside that iframe, so the
      // promise is restated through Stripe's own custom_text instead.
      const { client_secret } = await billingService.createCheckoutSession(
        `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        { restateTrialTerms: true },
      )
      setCheckoutClientSecret(client_secret ?? null)
      trackEvent(OnboardingWizardEvents.BILLING_CHECKOUT_MODAL_OPENED)
    } catch (error) {
      console.error('Failed to create checkout session:', error)
      trackEvent(OnboardingWizardEvents.BILLING_CHECKOUT_START_FAILED)
      setError(
        getApiErrorMessage(error, "We couldn't start your checkout. Please try again."),
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCheckoutClosed = (): void => {
    trackEvent(OnboardingWizardEvents.BILLING_CHECKOUT_MODAL_CLOSED)
    setCheckoutClientSecret(null)
  }

  const handleJoinTeam = async (invite: MyInviteResponse): Promise<void> => {
    setJoining(true)
    setJoinError(null)
    let alreadyMember = false
    try {
      await organizationService.acceptInvite(invite.id)
    } catch (error) {
      // 409 = already on a team (e.g. auto-joined at signup while this invite
      // was still pending) — billing is covered either way, so continue.
      if (getApiErrorStatus(error) !== 409) {
        console.error('Failed to accept team invite:', error)
        trackEvent(OnboardingWizardEvents.BILLING_TEAM_INVITE_ACCEPT_FAILED, {
          organization_id: invite.organization_id,
        })
        setJoinError(
          getApiErrorMessage(error, "We couldn't add you to the team. Please try again."),
        )
        setJoining(false)
        return
      }
      alreadyMember = true
    }
    trackEvent(OnboardingWizardEvents.BILLING_TEAM_INVITE_ACCEPTED, {
      organization_id: invite.organization_id,
      // Splits real accepts from the 409 already-on-a-team path in the funnel.
      already_member: alreadyMember,
    })
    onCompleted()
  }

  const handleSkipInvite = (): void => {
    if (teamInvite) {
      trackEvent(OnboardingWizardEvents.BILLING_TEAM_INVITE_SKIPPED, {
        organization_id: teamInvite.organization_id,
      })
    }
    setInviteSkipped(true)
  }

  // --- Paywall copy (the concluded onboarding-billing-value-framing 'test'
  // arm) -----------------------------------------------------------------
  // Trial length comes from the pricing endpoint (TRIAL_PERIOD_DAYS) rather
  // than a literal here, so the screen cannot drift from what checkout grants.
  // null while pricing is in flight, and also if it never arrives — in which
  // case the card drops the numbers instead of trapping the user behind a
  // permanently disabled button.
  const trialDays = pricing?.trial_period_days ?? null
  const channelLabel = connectedChannel ? CHANNEL_LABELS[connectedChannel] : null
  const assistantName = characterName?.trim() || 'Your assistant'
  // The last line is a real email, not reassurance-flavoured copy: the hourly
  // cron in trial_reminder_service finds trials ending in 24–25h and always
  // sends it for carded trials. Don't promise it here without that staying
  // true. It carries the emphasis because it is the one thing that answers
  // "what if I forget I signed up" — the fear this screen has to settle.
  const setupLines: { text: string, emphasis?: boolean }[] = [
    {
      text: channelLabel
        ? `Set up on ${channelLabel} — just send ${assistantName} a message`
        : `${assistantName} is set up and waiting on you`,
    },
    ...(hasMailCalendarScopes
      ? [{ text: 'Your calendar and inbox are already connected' }]
      : []),
    { text: 'Scheduling handled end to end — the back-and-forth, the invites, the reschedules' },
    { text: 'Inbox triage, drafts and follow-ups' },
    { text: "We'll email you the day before your trial ends", emphasis: true },
  ]
  // The first-charge date rides the price line rather than a separate block, so
  // "what you pay" and "when" read as one fact.
  const priceLine = pricing == null
    ? null
    : `then ${pricing.formatted}/${pricing.interval === 'year' ? 'yr' : 'mo'}`
      + `${trialDays == null ? '' : ` from ${formatFirstChargeDate(trialDays)}`}, cancel anytime`

  // Everything below the paywall card.
  const paywallFooter = (
    <>
      {teamInvite != null && (
        <div className="text-center mt-6">
          <button
            onClick={() => setInviteSkipped(false)}
            className="text-sm text-primary-600 hover:text-primary-700 underline"
          >
            {`Actually, join ${teamInvite.organization_name} instead`}
          </button>
        </div>
      )}

      {isLocalDev && (
        <div className="text-center mt-6">
          <button
            onClick={onCompleted}
            className="text-sm text-gray-400 hover:text-gray-600 underline"
          >
            Skip for now (local dev only)
          </button>
        </div>
      )}

      {checkoutClientSecret && (
        <CheckoutModal
          clientSecret={checkoutClientSecret}
          onClose={handleCheckoutClosed}
        />
      )}
    </>
  )

  if (showInviteOffer) {
    return (
      <div className="animate-slide-in-right">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-primary-600" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-display uppercase tracking-tight text-gray-900 mb-3">
            Join your team
          </h2>
          <p className="text-gray-500 text-lg">
            {teamInvite.organization_name} invited you to their team plan.
          </p>
        </div>

        <div className="bg-white rounded-xl border-2 border-primary-200 p-6 mb-6">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">{teamInvite.organization_name}</h3>
            <span className="text-sm font-medium text-primary-600">Team plan</span>
          </div>
          <ul className="space-y-2 mb-6">
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <Check className="w-4 h-4 text-primary-500 flex-shrink-0" />
              No card needed — billing is handled by your team
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <Check className="w-4 h-4 text-primary-500 flex-shrink-0" />
              Full access to your assistant, starting now
            </li>
          </ul>

          <button
            onClick={() => void handleJoinTeam(teamInvite)}
            disabled={joining}
            className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.96] shadow-soft hover:shadow-soft-lg disabled:opacity-50"
          >
            {joining ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              `Join ${teamInvite.organization_name}`
            )}
          </button>

          {joinError && (
            <p className="text-center text-sm text-red-600 mt-3" role="alert">
              {joinError}
            </p>
          )}
        </div>

        <div className="text-center">
          <button
            onClick={handleSkipInvite}
            className="text-sm text-gray-400 hover:text-gray-600 underline"
          >
            Skip — set up my own billing instead
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-slide-in-right">
      <div className="text-center mb-8">
        <h2 className="text-3xl sm:text-4xl font-display uppercase tracking-tight text-gray-900 mb-3">
          {`${assistantName} is ready when you are`}
        </h2>
        <p className="text-gray-500 text-lg">
          Start your free trial — no charge today.
        </p>
      </div>

      <div className="bg-white rounded-xl border-2 border-primary-200 p-6 mb-6">
        {trialDays == null ? (
          <div className="mb-6" aria-hidden="true">
            <div className="h-9 sm:h-10 w-52 max-w-full rounded bg-gray-200 animate-pulse" />
            <div className="h-5 w-40 max-w-full rounded bg-gray-100 animate-pulse mt-2" />
          </div>
        ) : (
          <div className="mb-6">
            <p className="text-3xl sm:text-4xl font-bold text-gray-900">
              {`Free for ${trialDays} ${trialDays === 1 ? 'day' : 'days'}`}
            </p>
            <p className="text-gray-500 mt-1">
              {priceLine}
            </p>
          </div>
        )}

        <ul className="space-y-2 mb-6">
          {setupLines.map((line) => (
            <li
              key={line.text}
              className={`flex items-start gap-2 text-sm ${
                line.emphasis ? 'text-gray-900 font-medium' : 'text-gray-700'
              }`}
            >
              <Check className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" />
              {line.text}
            </li>
          ))}
        </ul>

        <button
          onClick={() => void handleStartTrial()}
          disabled={loading || pricingLoading}
          className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.96] shadow-soft hover:shadow-soft-lg disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            trialDays == null ? 'Start free trial' : `Start ${trialDays}-day free trial`
          )}
        </button>

        {error && (
          <p className="text-center text-sm text-red-600 mt-3" role="alert">
            {error}
          </p>
        )}
      </div>

      {paywallFooter}
    </div>
  )
}
